'use client';

import { ShowcaseIcon } from '../../collection-showcase/scenes/scene-primitives.jsx';
import { BlurSwap } from '../../collection-showcase/scenes/playback-controls.jsx';
import { SegmentedControl } from '../../collection-showcase/scenes/playback-controls.jsx';
import { useMostlyInView } from '../../collection-showcase/use-animation-clock';

// Recovered from the IMAI v5 animation source; demo documents stay illustrative.
import * as React from 'react';
import {
  ShowcaseCopyContext,
  translateShowcaseNode,
} from '../../collection-showcase/translation';
import {
  useReducedMotion,
  usePlaybackClock,
} from '../../collection-showcase/use-animation-clock';
import { quickTimeline, jn, Mn, researchSources } from './research-facts';
import {
  deepTimeline,
  Gn,
  Kn,
  DeepResearch,
  CitationConnectors,
} from './deep-research';
import { QuickAnswer, PriceComparison } from './quick-answer';
export function ResearchAnimation() {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let e = useReducedMotion(),
    [t, n] = React.useState('quick'),
    [r, i] = React.useState(null),
    [a, o] = useMostlyInView(0.55),
    s = React.useRef(null),
    c = React.useRef(null),
    l = React.useRef(null),
    {
      t: u,
      done: f,
      restart: p,
    } = usePlaybackClock({
      duration: t === 'quick' ? quickTimeline.end : deepTimeline.end,
      running: o === !0,
      reduced: e,
    }),
    m = t === 'quick' ? jn(u) : [],
    h = t === 'quick' ? Mn(u) : deepTimeline.rowsBy(u),
    g =
      t === 'quick'
        ? u >= quickTimeline.streamEnd - 600
        : u >= deepTimeline.recommend,
    ee = r ?? (t === 'quick' && !f ? (m[m.length - 1] ?? null) : null);
  return (
    <div ref={a} className={'sf mt-10 rounded-[20px] p-2 md:mt-14'}>
      <div
        className={
          'flex flex-wrap items-center gap-x-4 gap-y-2 px-2 pt-1 pb-3 sm:px-3'
        }
      >
        <SegmentedControl
          options={Gn}
          value={t}
          onChange={(e) => {
            e !== t && (n(e), i(null), p());
          }}
          label={'Research depth'}
          size={'sm'}
        />
        <p
          className={
            'text-ink-3 m-0 min-w-0 flex-1 text-[13px] max-sm:order-last max-sm:basis-full max-sm:px-1'
          }
        >
          <BlurSwap value={Kn[t]} />
        </p>
        <button
          type={'button'}
          onClick={p}
          disabled={!f}
          className={
            'ghost press flex h-8 items-center gap-1.5 px-2.5 text-[13px] transition-opacity duration-200 disabled:pointer-events-none disabled:opacity-0 max-sm:ml-auto'
          }
          aria-label={translateShowcaseNode(
            showcaseCopy,
            'Replay the research'
          )}
        >
          <ShowcaseIcon name={'replay'} className={'size-4'} />
          <span className={'max-sm:sr-only'}>
            {translateShowcaseNode(showcaseCopy, 'Replay')}
          </span>
        </button>
      </div>
      <div
        ref={s}
        className={
          'relative grid gap-6 px-2 pb-2 sm:px-3 lg:grid-cols-[minmax(0,1fr)_64px_minmax(0,1.08fr)] lg:gap-0'
        }
      >
        <div key={t} ref={c} className={'min-w-0 py-1'}>
          {translateShowcaseNode(
            showcaseCopy,
            t === 'quick' ? (
              <QuickAnswer t={u} focus={ee} onFocus={i} />
            ) : (
              <DeepResearch t={u} focus={ee} onFocus={i} />
            )
          )}
        </div>
        <div aria-hidden={!0} className={'max-lg:hidden'} />
        <div ref={l} className={'min-w-0'}>
          <PriceComparison shown={h} band={g} focus={ee} onFocus={i} />
        </div>
        <CitationConnectors
          root={s}
          from={c}
          to={l}
          version={`${t}-${m.length}-${h}-${Math.floor(u / 210)}`}
          focus={ee}
        />
      </div>
      <p className={'sr-only'} aria-live={'polite'}>
        {translateShowcaseNode(
          showcaseCopy,
          f
            ? `${researchSources.length} sources checked. Every price links to its source.`
            : ''
        )}
      </p>
    </div>
  );
}
export function TypedText({ text: e, shown: t }) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let n = Math.max(0, Math.min(e.length, t));
  return n >= e.length ? (
    <React.Fragment>{translateShowcaseNode(showcaseCopy, e)}</React.Fragment>
  ) : (
    <React.Fragment>
      {translateShowcaseNode(showcaseCopy, e.slice(0, n))}
      <span className={'text-transparent'} aria-hidden={!0}>
        {translateShowcaseNode(showcaseCopy, e.slice(n))}
      </span>
    </React.Fragment>
  );
}
export function oh(e, t, n, r) {
  return t < n ? 0 : Math.min(e.length, Math.floor((t - n) / r) + 1);
}
export function sh(e, t, n) {
  return e <= t ? 0 : e >= n ? 1 : (e - t) / (n - t);
}
export function useTeamPlayback(e) {
  let t = useReducedMotion(),
    [n, r] = useMostlyInView(0.55);
  return {
    ref: n,
    inView: r,
    reduced: t,
    ...usePlaybackClock({
      duration: e,
      running: r === !0,
      reduced: t,
    }),
  };
}
export function ReplayButton({
  onReplay: e,
  label: t = 'Replay',
  describedBy: n,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <button
      type={'button'}
      onClick={e}
      aria-describedby={n}
      className={
        'ghost press flex h-8 shrink-0 items-center gap-1.5 px-2.5 text-[13px] font-medium'
      }
    >
      <ShowcaseIcon name={'replay'} className={'size-4'} />
      {translateShowcaseNode(showcaseCopy, t)}
    </button>
  );
}
export function PlaybackProgress({ value: e }) {
  return (
    <span
      aria-hidden={!0}
      className={
        'block h-[2px] flex-1 overflow-hidden rounded-full bg-[var(--state-wash)]'
      }
    >
      <span
        className={'block h-full origin-left bg-[var(--primary)]'}
        style={{
          transform: `scaleX(${e})`,
        }}
      />
    </span>
  );
}
export function TeamAvatar({
  person: e,
  size: t = 28,
  className: n = '',
  style: r,
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  return (
    <span
      role={'img'}
      aria-label={translateShowcaseNode(showcaseCopy, e.name)}
      className={`inline-grid shrink-0 place-items-center rounded-full font-medium select-none ${n}`}
      style={{
        width: t,
        height: t,
        background: e.bg,
        color: e.ink,
        fontSize: Math.round(t * 0.38),
        letterSpacing: '0.01em',
        lineHeight: 1,
        ...r,
      }}
    >
      {translateShowcaseNode(showcaseCopy, e.initials)}
    </span>
  );
}
export function TeamAvatarStack({
  people: e,
  size: t = 16,
  ring: n = 'var(--panel)',
  max: r = 4,
  className: i = '',
}) {
  const showcaseCopy = React.use(ShowcaseCopyContext);
  let a = e.length > r ? e.slice(0, r - 1) : e,
    o = e.length - a.length,
    s = t <= 16 ? -6 : 0;
  return (
    <span className={`flex ${i}`}>
      {translateShowcaseNode(
        showcaseCopy,
        a.map((e, r) => (
          <span
            key={e.key}
            className={'tr-face inline-flex rounded-full'}
            style={{
              boxShadow: `0 0 0 2px ${n}`,
              marginInlineStart: r === 0 ? 0 : s,
            }}
          >
            <TeamAvatar person={e} size={t} />
          </span>
        ))
      )}
      {translateShowcaseNode(
        showcaseCopy,
        o > 0 ? (
          <span
            className={
              'num text-ink-3 inline-grid place-items-center rounded-full bg-[var(--accent-bg)] font-medium'
            }
            style={{
              width: t,
              height: t,
              fontSize: Math.round(t * 0.42),
              marginInlineStart: s,
              boxShadow: `0 0 0 2px ${n}`,
            }}
          >
            {translateShowcaseNode(showcaseCopy, '+')}
            {translateShowcaseNode(showcaseCopy, o)}
          </span>
        ) : null
      )}
    </span>
  );
}
export const teamPeople = {
  nadia: {
    key: 'nadia',
    name: 'Nadia Laurent',
    first: 'Nadia',
    initials: 'NL',
    role: 'Founder',
    bg: '#EEEDFD',
    ink: '#4409B9',
  },
  maya: {
    key: 'maya',
    name: 'Maya Okafor',
    first: 'Maya',
    initials: 'MO',
    role: 'Merchandising',
    bg: '#FDEFE2',
    ink: '#C56511',
  },
  priya: {
    key: 'priya',
    name: 'Priya Raman',
    first: 'Priya',
    initials: 'PR',
    role: 'Production',
    bg: '#ECFAFE',
    ink: '#1F84A3',
  },
  theo: {
    key: 'theo',
    name: 'Theo Brandt',
    first: 'Theo',
    initials: 'TB',
    role: 'Design assistant',
    bg: '#E7F9F3',
    ink: '#216E55',
  },
  jonah: {
    key: 'jonah',
    name: 'Jonah Weiss',
    first: 'Jonah',
    initials: 'JW',
    role: 'Marketing',
    bg: '#FFEBEE',
    ink: '#BD0F2C',
  },
};
export const ph = [
  teamPeople.nadia,
  teamPeople.maya,
  teamPeople.priya,
  teamPeople.theo,
  teamPeople.jonah,
];
export const mh = {
  name: 'lisiere-fw27',
  topic: 'FW27 capsule \xB7 tailoring and knitwear',
};
